import { Button, Drawer, DrawerFooter, DrawerHeader } from '@/design-system';
import { primaryRole } from '@/entities/user';
import { errorMessage, ROLE_LABELS, type Role } from '@/shared/api';

import { inspectorPickerCopy } from './copy';
import { useTakeOnFieldRole } from './hooks';

export interface TakeOnRolePanelProps {
    open: boolean;
    onClose: () => void;
    /** Je rollen nu; de zwaarste blijft, en die noemt het paneel. */
    roles: readonly Role[];
}

/**
 * "Ook als inspecteur werken?" - de korte vraag aan wie zichzelf kiest zonder rol
 * op locatie, zoals de eigenaar die zelf keurt.
 *
 * Ja geeft je de rol inspecteur erbij (`POST /users/me/roles/inspector`); je
 * andere rollen blijven. Nee laat de keuze staan: de API neemt elk actief lid aan,
 * alleen verschijnt de opname dan niet in Mijn dag.
 */
export function TakeOnRolePanel({ open, onClose, roles }: TakeOnRolePanelProps) {
    const copy = inspectorPickerCopy.takeOn;

    return (
        <Drawer open={open} onClose={onClose} side="right" width={440} title={copy.title}>
            <DrawerHeader
                icon="myDay"
                title={copy.title}
                description={copy.description}
                onClose={onClose}
                closeLabel={copy.close}
            />
            {/* Een dichte Drawer rendert niets: elke keer openen begint bij de vraag. */}
            <TakeOnRoleQuestion roles={roles} onClose={onClose} />
        </Drawer>
    );
}

function TakeOnRoleQuestion({ roles, onClose }: { roles: readonly Role[]; onClose: () => void }) {
    const copy = inspectorPickerCopy.takeOn;
    const takeOn = useTakeOnFieldRole();
    const kept = primaryRole(roles);

    if (takeOn.isSuccess) {
        return (
            <>
                <div className="flex flex-col gap-3 p-5">
                    <p
                        role="status"
                        className="m-0 text-[14px] leading-[1.5] text-[var(--accent-dark)]"
                    >
                        {copy.done}
                    </p>
                </div>
                <DrawerFooter>
                    <Button variant="primary" size="md" onClick={onClose}>
                        {copy.finish}
                    </Button>
                </DrawerFooter>
            </>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-3 p-5">
                <ul className="m-0 list-none p-0">
                    {copy.gains(kept ? ROLE_LABELS[kept] : ROLE_LABELS.inspector).map((line) => (
                        <li
                            key={line}
                            className="flex gap-2 py-[3px] text-[14px] leading-[1.5] text-[var(--text-primary)]"
                        >
                            <span
                                aria-hidden="true"
                                className="mt-[8px] h-[5px] w-[5px] shrink-0 rounded-full bg-[var(--accent)]"
                            />
                            {line}
                        </li>
                    ))}
                </ul>

                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {copy.without}
                </p>

                {takeOn.error ? (
                    <p role="alert" className="m-0 text-[13px] leading-[1.5] text-[var(--danger)]">
                        {errorMessage(takeOn.error)}
                    </p>
                ) : null}
            </div>

            <DrawerFooter>
                <Button
                    variant="primary"
                    size="md"
                    disabled={takeOn.isPending}
                    onClick={() => takeOn.mutate('inspector')}
                >
                    {takeOn.isPending ? copy.busy : copy.confirm}
                </Button>
                <Button variant="outline" size="md" onClick={onClose}>
                    {copy.decline}
                </Button>
            </DrawerFooter>
        </>
    );
}
